import { SERVICE_LABOR_SECTION_TITLE } from '@/components/molecules/ServiceLaborCostFields';
import {
  DEFAULT_LABOR_LINE,
  LaborLinesEditor,
  laborLinesHours,
  laborLinesTotal,
} from '@/components/organisms/pricebook/LaborLinesEditor';
import { flatServiceLaborLines, freezeMarginShares } from '@/lib/sales/service-labor';
import type { LaborRateProfile, ServiceLaborLine } from '@/types/pricebook';

/**
 * WHAT A FLAT SERVICE ON A DOCUMENT COSTS: its time.
 *
 * A service's cost is the labor it takes — hours x the hourly rate, per unit,
 * times the quantity — plus its parts on their own lines (SPEC-line-item-model,
 * "Flat vs Dynamic"). There is no unit cost on a service, so this is the one
 * cost input a service line offers, on every document that edits one: the
 * estimate's service row and the job's line editor draw this same block, with
 * the same rows, heading and reduction, so the two cannot drift.
 *
 * A line saved before the rows existed carries only the reduced pair, and is
 * reconstructed through `flatServiceLaborLines` — the same reconstruction the
 * margin panel reads. One blank row is added when there is no labor at all:
 * an editor with nothing in it gives the operator nowhere to start, and the row
 * costs nothing until it is filled.
 *
 * `onChange` hands back the rows, the hours they total and what they are worth
 * — the shape `laborCostFields` reduces into the line's saved fields.
 */
export function ServiceLaborCostEditor({
  line,
  profiles,
  onChange,
}: {
  line: {
    laborCostLines?: ServiceLaborLine[] | null;
    estimatedHours?: number | null;
    laborCostRate?: number | null;
  };
  profiles: LaborRateProfile[];
  onChange: (lines: ServiceLaborLine[], hours: number, cost: number) => void;
}) {
  const recorded = flatServiceLaborLines(line);
  const rows: ServiceLaborLine[] = recorded.length
    ? recorded
    : [{ ...DEFAULT_LABOR_LINE, durationMinutes: 0 }];

  return (
    <div className="space-y-2 rounded-md border border-dashed bg-background/70 p-3">
      <p className="text-xs font-medium">{SERVICE_LABOR_SECTION_TITLE}</p>
      <LaborLinesEditor
        basis="COST"
        lines={rows}
        profiles={profiles}
        onChange={(lines) =>
          onChange(
            // Frozen where the catalog rate becomes this document's rate; see
            // `freezeMarginShares`.
            freezeMarginShares(lines, profiles),
            // HOURLY hours and HOURLY money: a margin share quotes no time and
            // is re-derived by the document's costing from the frozen
            // percentage above, against a contribution only the whole document
            // knows.
            laborLinesHours(lines),
            laborLinesTotal(lines, profiles),
          )
        }
      />
    </div>
  );
}
